'use client';

import { useSearchCommand } from '@hooks/use-search-command';
import { getHref, ROUTE } from '@utils/route-utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog';

export function NavigationShortcuts({
  organization_id
}: {
  organization_id: string;
}) {
  const router = useRouter();
  const t = useTranslations('shortcuts');
  const { setOpen: setSearchOpen } = useSearchCommand();
  const [helpOpen, setHelpOpen] = useState(false);
  const chordStartedAt = useRef(0);

  useEffect(() => {
    const destinations: Record<string, string> = {
      h: getHref(organization_id, [ROUTE.HOME]),
      e: getHref(organization_id, [ROUTE.EIGHTY_SIX]),
      l: getHref(organization_id, [ROUTE.LIBRARY]),
      m: getHref(organization_id, [ROUTE.MESSAGES])
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        target?.matches('input, textarea, select')
      ) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === '/') {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }

      if (event.key === '?') {
        event.preventDefault();
        setHelpOpen(true);
        return;
      }

      const key = event.key.toLowerCase();
      if (key === 'g') {
        chordStartedAt.current = performance.now();
        return;
      }

      const destination = destinations[key];
      if (destination && performance.now() - chordStartedAt.current < 800) {
        event.preventDefault();
        chordStartedAt.current = 0;
        router.push(destination);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [organization_id, router, setSearchOpen]);

  const shortcuts = [
    ['/', t('search')],
    ['⌘ K', t('search')],
    ['G H', t('today')],
    ['G E', t('eighty-six')],
    ['G L', t('library')],
    ['G M', t('messages')],
    ['⌘ B', t('sidebar')],
    ['?', t('help')]
  ];

  return (
    <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <dl className="divide-y divide-border">
          {shortcuts.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between py-3">
              <dt className="text-body-dense text-secondary-foreground">
                {label}
              </dt>
              <dd>
                <kbd className="rounded-md border bg-muted px-2 py-1 font-mono text-meta text-foreground">
                  {key}
                </kbd>
              </dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}
